/**
 * Pone un plazo máximo a una promesa.
 *
 * POR QUÉ EXISTE
 * No toda promesa que va mal termina rechazando. Varias APIs del navegador
 * pueden quedarse PENDIENTES PARA SIEMPRE, y por especificación nunca se
 * rechazan:
 *
 *   - `navigator.serviceWorker.ready` cuando no hay ningún worker registrado;
 *   - `PushManager.subscribe()` si el servicio de push del navegador no
 *     responde;
 *   - una petición `fetch` sin `AbortSignal` con la red caída del todo.
 *
 * Un `await` sobre una de ellas no lanza, no registra nada y no vuelve: el
 * `try/catch` de alrededor jamás se ejecuta y la función queda detenida en esa
 * línea. Para el usuario eso se ve como un botón que no hace nada, sin error ni
 * rastro en la consola. Es un fallo especialmente caro de diagnosticar porque
 * no deja ninguna huella.
 *
 * `withTimeout` convierte ese cuelgue mudo en un rechazo normal, que el código
 * de alrededor ya sabe registrar y manejar.
 *
 * IMPORTANTE: no CANCELA el trabajo subyacente, solo deja de esperarlo. Para
 * operaciones que además haya que abortar de verdad (una petición HTTP, por
 * ejemplo) hay que usar `AbortController`.
 */

/** Rechazo específico del plazo agotado, distinguible de un fallo real. */
class TimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "TimeoutError";
  }
}

/**
 * Devuelve una promesa que se resuelve como `promise`, o que rechaza con
 * `TimeoutError` si tarda más de `ms` milisegundos.
 *
 * @param label Texto que aparece en el mensaje de error para saber QUÉ se
 *   colgó. Sin él, todos los plazos agotados dan el mismo error anónimo.
 */
export function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  label = "la operación",
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new TimeoutError(`Se agotó el plazo de ${ms} ms esperando ${label}`));
    }, ms);

    promise.then(
      (value) => {
        // Limpiar el temporizador siempre: si no, el proceso mantiene vivo un
        // timer inútil por cada llamada.
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}
